import { useTranslation } from "react-i18next";
import type { RunDetail } from "../../api/hooks";
import { ErrorState } from "../../components/states";
import { Card } from "../../components/ui/card";
import { formatValue, humanize } from "../../lib/format";
import { DataTable } from "./data-table";
import { useUiStore } from "../../stores/ui-store";
import { AgentTrace } from "./agent-trace";

type Row = Record<string, unknown>;

function isPlainObject(value: unknown): value is Row {
  return typeof value === "object" && value !== null && !Array.isArray(value);
}

function isRecordList(value: unknown): value is Row[] {
  return Array.isArray(value) && value.length > 0 && value.every(isPlainObject);
}

/** Renders report data: records as tables, objects as labeled sections, values formatted. */
export function DataView({ value, title, depth = 0 }: { value: unknown; title?: string; depth?: number }) {
  if (isRecordList(value)) {
    return (
      <section className="space-y-2">
        {title ? <h3 className="font-semibold">{title}</h3> : null}
        <DataTable rows={value} caption={title} />
      </section>
    );
  }
  if (isPlainObject(value)) {
    const entries = Object.entries(value);
    const simple = entries.filter(([, inner]) => !isRecordList(inner) && !isPlainObject(inner));
    const nested = entries.filter(([, inner]) => isRecordList(inner) || isPlainObject(inner));
    return (
      <section className="space-y-4">
        {title ? <h3 className={depth === 0 ? "text-lg font-semibold" : "font-semibold"}>{title}</h3> : null}
        {simple.length > 0 ? (
          <dl className="grid grid-cols-[minmax(8rem,auto)_1fr] gap-x-6 gap-y-1 text-sm">
            {simple.map(([key, inner]) => (
              <div key={key} className="contents">
                <dt className="text-muted-foreground">{humanize(key)}</dt>
                <dd className={typeof inner === "number" ? "tabular-nums" : ""}>{formatValue(inner)}</dd>
              </div>
            ))}
          </dl>
        ) : null}
        {nested.map(([key, inner]) => (
          <DataView key={key} value={inner} title={humanize(key)} depth={depth + 1} />
        ))}
      </section>
    );
  }
  return <p className="text-sm">{formatValue(value)}</p>;
}

export function ReportView({ run }: { run: RunDetail }) {
  const { t } = useTranslation();
  const theme = useUiStore((state) => state.theme);
  return (
    <div className="space-y-6">
      <AgentTrace key={run.id} run={run} />
      {run.status === "failed" ? <ErrorState title={t("report.failed")} error={new Error(run.error ? `${run.error.message} (${run.error.code})` : "")} /> : null}
      {run.status === "insufficient" && run.insufficient ? (
        <Card className="space-y-1 border-warning/60 bg-warning/10">
          <p className="font-medium">{t("report.insufficient")}</p>
          <p className="text-sm">{run.insufficient.reason}</p>
          <p className="text-sm text-muted-foreground">{t("report.missing", { missing: run.insufficient.missing.join(", ") })}</p>
        </Card>
      ) : null}
      {run.reportAvailable ? <iframe title={t("report.agentPage")} src={`/api/v1/runs/${run.id}/report?theme=${theme}`} sandbox="allow-scripts" className="min-h-[42rem] w-full rounded-lg border border-border bg-background" /> : run.status === "succeeded" ? <DataView value={run.data} /> : null}
      {run.receipt ? <p className="text-xs text-muted-foreground">{t("run.receipt", run.receipt)}</p> : null}
      {run.status === "succeeded" ? (
        <details className="text-sm">
          <summary className="cursor-pointer text-muted-foreground">{t("report.raw")}</summary>
          <pre className="mt-2 max-h-96 overflow-auto rounded-md bg-muted p-3 text-xs">{JSON.stringify(run.data, null, 2)}</pre>
        </details>
      ) : null}
      {run.log ? (
        <details className="text-sm">
          <summary className="cursor-pointer text-muted-foreground">{t("report.log")}</summary>
          <pre className="mt-2 max-h-64 overflow-auto rounded-md bg-muted p-3 text-xs">{run.log}</pre>
        </details>
      ) : null}
    </div>
  );
}
